import EncryptedStorage from 'core/utils/constants/encryptedStorage';
import axios from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { error, success, warning } from 'core/components/Notify';
import {
  resetLocalStorage,
  setTokenToLocalStorage,
} from 'features/Authentication/slice/saga';
import { JWTPayload } from 'features/Authentication/slice/types';
import { startRequest, endRequest } from './requestProgress';
import { API_HOST } from 'core/utils/constants/ApiConstants';
import { addPageSize, ErrorMessageFunction, isFileUpload, trimPayload } from 'core/utils/constants';
import { refreshStoredGrantedRoleAccess } from 'core/utils/accessControl';

const baseURL = API_HOST;

interface CustomProps {
  ipAddress?: string;
  dbName?: string;
  contentType?: string;
  accept?: string;
  token?: string;
  otherKeys?: string;
}

const getCurrentPath = () => {
  return window.location.pathname + window.location.search;
};

// A branch with no active plan is authenticated but gated: its token is
// tagged RESTRICTED and every feature endpoint refuses it with this code.
const isSubscriptionRestricted = (err: any) => {
  if (err?.response?.status !== 403) return false;
  const detail = err?.response?.data?.detail ?? err?.response?.data;
  const errors = detail?.error;
  return (
    Array.isArray(errors) &&
    errors.some((item: any) => item?.code === 'SUBSCRIPTION_RESTRICTED')
  );
};

const SUBSCRIPTION_PATH = '/subscription';

export const SUBSCRIPTION_GATE_FLAG = '_SUB_GATED_';

// Every gated call fails, so this caps it at one toast, not a dozen.
let subscriptionNoticeShownAt = 0;
const SUBSCRIPTION_NOTICE_INTERVAL_MS = 30_000;

const handleSubscriptionRestricted = (err: any) => {
  const alreadyThere = window.location.pathname.startsWith(SUBSCRIPTION_PATH);

  const now = Date.now();
  if (now - subscriptionNoticeShownAt >= SUBSCRIPTION_NOTICE_INTERVAL_MS) {
    subscriptionNoticeShownAt = now;
    const detail = err?.response?.data?.detail ?? err?.response?.data;
    warning(
      detail?.message || 'This branch does not have an active subscription yet.',
      true,
    );
  }

  // Never bounce the subscription page itself, or its calls would loop.
  if (!alreadyThere) {
    try {
      sessionStorage.setItem(SUBSCRIPTION_GATE_FLAG, '1');
    } catch {}
    window.location.href = SUBSCRIPTION_PATH;
  }
};

const currentPath = getCurrentPath();

const axiosInstance: any = axios.create({
  baseURL,
});

const axiosInstancenew: any = axios.create({
  baseURL,
});

function renderHeader(headers) {
  if (headers?.['Custom-Header'] === 'multipart/form-data') {
    return 'multipart/form-data';
  } else {
    return 'application/json';
  }
}

// Use an array to keep track of cancel tokens
const cancelTokens: any = [];

// Cache for frequently accessed encrypted data
class HeaderCache {
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  get(key: string) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data;
    }
    return null;
  }

  set(key: string, data: any) {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  clear() {
    this.cache.clear();
  }

  // Clean expired entries
  cleanup() {
    const now = Date.now();
    for (const [key, value] of (this as any)?.cache?.entries()) {
      if (now - value.timestamp >= this.CACHE_TTL) {
        this.cache.delete(key);
      }
    }
  }
}

const headerCache = new HeaderCache();

// Clean cache every 5 minutes
setInterval(() => {
  headerCache.cleanup();
}, 5 * 60 * 1000);

// Own readers rather than httpclient()'s, so this stays additive.
const readStoredItem = (key: string) => {
  let cached = headerCache.get(key);
  if (cached === null) {
    cached = EncryptedStorage.getItem(key);
    if (cached !== null) {
      headerCache.set(key, cached);
    }
  }
  return cached;
};

const readStoredUserData = (): any => {
  try {
    const userData = EncryptedStorage.getItem('_DATA_');
    if (userData) {
      return JSON.parse(userData as string);
    }
  } catch (err) {
    console.error('Failed to parse user data:', err);
  }

  return null;
};

// Reproduces the headers the bypassed request interceptor would have attached.
const buildRefreshHeaders = (userData: any, customProps?: CustomProps) => {
  const refreshHeaders: Record<string, any> = {
    Authorization: `Bearer ${userData.REFRESH_TOKEN}`,
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'database': customProps?.dbName || readStoredItem('_DATABASE_') || '',
    'SOURCE': 'STAFF',
    'Ip-Address': customProps?.ipAddress || readStoredItem('IP_ADDRESS') || '',
  };

  if (userData.PAYMENT_SERVICE_NAME) {
    refreshHeaders['payment-service-name'] = userData.PAYMENT_SERVICE_NAME;
  }
  if (userData.INSTITUTE_ID) {
    refreshHeaders['institute-id'] = userData.INSTITUTE_ID;
  }
  if (userData.USER_NAME) {
    refreshHeaders['USER-NAME'] = userData.USER_NAME;
  }
  if (userData.USER_ROLE_NAME) {
    refreshHeaders['USER-ROLE'] = userData.USER_ROLE_NAME;
  }
  if (userData.STAFF_ROLE?.STAFF_ROLE_NAME) {
    refreshHeaders['STAFF-ROLE-NAME'] = userData.STAFF_ROLE.STAFF_ROLE_NAME;
  }
  if (userData.BRANCH_ID) {
    refreshHeaders['branch-id'] = userData.BRANCH_ID;
  }
  if (userData.USER_ID) {
    refreshHeaders['USER-ID'] = userData.USER_ID;
  }
  if (userData.BRANCH_DID_NUMBER) {
    refreshHeaders['branch-did-number'] = userData.BRANCH_DID_NUMBER;
  }

  // Handle academic year
  const selectedAcademicYear = readStoredItem('_SELECTED_ACADEMIC_YEAR_');
  let academicYearId = null;
  if (selectedAcademicYear) {
    try {
      academicYearId = JSON.parse(selectedAcademicYear as string)?.ACADEMIC_YEAR_ID;
    } catch (e) { }
  }
  if (!academicYearId && userData?.CURRENT_ACADEMIC_YEAR_ID) {
    academicYearId = userData.CURRENT_ACADEMIC_YEAR_ID;
  }
  if (academicYearId) {
    refreshHeaders['academic-year-id'] = academicYearId;
  }

  return refreshHeaders;
};

/**
 * Mint a fresh access token from the stored refresh token. Null if no session.
 *
 * Must post through `axiosInstancenew`: `httpclient()` stacks an interceptor
 * per call and axios runs them newest-first, so the oldest — holding no custom
 * props — overwrites `Authorization` with the access token, which this
 * endpoint rejects as the wrong type.
 */
export const refreshAccessToken = async (
  customProps?: CustomProps,
): Promise<string | null> => {
  const userData: any = readStoredUserData();

  if (!userData?.REFRESH_TOKEN || !userData?.USER_ID || !userData?.USER_LOGIN_ID) {
    return null;
  }

  const response = await axiosInstancenew.post(
    `/auth/refreshToken`,
    {
      USER_ID: userData.USER_ID,
      USER_LOGIN_ID: userData.USER_LOGIN_ID,
    },
    {
      headers: buildRefreshHeaders(userData, customProps),
    },
  );

  const newToken = response?.data?.data?.new_token;
  if (!newToken) {
    return null;
  }

  setTokenToLocalStorage(newToken);
  // Or the cache serves the replaced token for the rest of its TTL.
  headerCache.set('_UFT_', `Bearer ${newToken}`);

  return newToken;
};

const httpclient = (customProps?: CustomProps | any) => {

  // User data can change during role switching; always read it fresh for headers.
  function getCachedUserData(): JWTPayload | null {
    try {
      const userData = EncryptedStorage.getItem('_DATA_');
      if (userData) {
        return JSON.parse(userData as string);
      }
    } catch (error) {
      console.error('Failed to parse user data:', error);
    }

    return null;
  }

  // Function to get cached data with fallback
  function getCachedItem(key: string) {
    let cached = headerCache.get(key);
    if (cached === null) {
      cached = EncryptedStorage.getItem(key);
      if (cached !== null) {
        headerCache.set(key, cached);
      }
    }
    return cached;
  }

  // Obtain the fresh token each time the function is called
  function getAccessToken() {
    return getCachedItem('_UFT_');
  }

  // Preload frequently accessed items into cache
  const preloadCache = () => {
    const keysToPreload = [
      '_UFT_',
      '_DATABASE_',
      '_DATA_',
      '_SELECTED_ACADEMIC_YEAR_',
      'IP_ADDRESS'
    ];

    // Use batch loading for better performance
    const cachedData = EncryptedStorage.getMultipleItems(keysToPreload);
    keysToPreload.forEach(key => {
      if (cachedData[key] !== null) {
        headerCache.set(key, cachedData[key]);
      }
    });
  };

  // Preload cache on initialization
  preloadCache();

  // Use interceptor to inject the token to requests
  axiosInstance.interceptors.request.use(async (request: any) => {
    startRequest();

    // Get cached user data once per request
    const userData = getCachedUserData();

    // Only apply trimPayload if it's not a file upload
    if (request.data && !isFileUpload(request.data)) {
      request.data = trimPayload(request.data);
    }

    if ((request?.url.includes('/list') || request?.url.includes('/view')) &&
      !request?.url?.includes("tableColumnDisplay/list") &&
      !request?.url?.includes("examResults/list") &&
      !request?.url?.includes('/master/blog/list') &&
      !request?.url?.includes('/master/blog/view')) {
      if (!request?.url?.includes('page_size')) {
        request.url = addPageSize(request?.url);
      }
    }

    const source = axios.CancelToken.source();
    request.cancelToken = source.token;
    cancelTokens.push(source);

    // Set headers using cached values
    if (getAccessToken() || customProps?.token) {
      request.headers['Authorization'] = customProps?.token || `${getAccessToken()}`;
    }

    // Fallback for database header
    let database = customProps?.dbName || getCachedItem('_DATABASE_');
    if (!database && userData?.USER_INFO?.[0]?.DATABASE) {
      database = userData.USER_INFO[0].DATABASE;
    }
    request.headers['database'] = database;
    request.headers['Accept'] = renderHeader(request?.headers);
    request.headers['Content-Type'] = renderHeader(request?.headers);

    // Use cached user data for all header assignments
    if (userData) {
      try {
        const info = (userData.USER_INFO && userData.USER_INFO.length > 0) ? userData.USER_INFO[0] : {};

        const paymentService = userData.PAYMENT_SERVICE_NAME || info.PAYMENT_SERVICE_NAME;
        if (paymentService) {
          request.headers['payment-service-name'] = paymentService;
        }

        const instituteId = userData.INSTITUTE_ID || info.INSTITUTE_ID;
        if (instituteId) {
          request.headers['institute-id'] = instituteId;
        }

        const userName = userData.USER_NAME || info.USER_NAME;
        if (userName) {
          request.headers['USER-NAME'] = userName;
        }

        const userRole = userData.USER_ROLE_NAME || info.USER_ROLE_NAME;
        if (userRole) {
          request.headers['USER-ROLE'] = userRole;
        }

        if (userData.STAFF_ROLE?.STAFF_ROLE_NAME) {
          request.headers['STAFF-ROLE-NAME'] = userData.STAFF_ROLE.STAFF_ROLE_NAME;
        } else if (info.STAFF_ROLE?.STAFF_ROLE_NAME) {
          request.headers['STAFF-ROLE-NAME'] = info.STAFF_ROLE.STAFF_ROLE_NAME;
        }

        const branchId = userData.BRANCH_ID || info.BRANCH_ID;
        if (branchId) {
          request.headers['branch-id'] = branchId;
        }

        const userId = userData.USER_ID || info.USER_ID;
        if (userId) {
          request.headers['USER-ID'] = userId;
        }

        const branchDid = userData.BRANCH_DID_NUMBER || info.BRANCH_DID_NUMBER;
        if (branchDid) {
          request.headers['branch-did-number'] = branchDid;
        }
      } catch (err) {
        console.error('Error setting headers from user data:', err);
      }
    }

    // Handle academic year - check selected first, then current
    const selectedAcademicYear = getCachedItem('_SELECTED_ACADEMIC_YEAR_');
    let academicYearId = null;

    if (selectedAcademicYear) {
      try {
        academicYearId = JSON.parse(selectedAcademicYear as string)?.ACADEMIC_YEAR_ID;
      } catch (e) { }
    }

    if (!academicYearId) {
      if (userData?.CURRENT_ACADEMIC_YEAR_ID) {
        academicYearId = userData.CURRENT_ACADEMIC_YEAR_ID;
      } else if (userData?.USER_INFO?.[0]?.CURRENT_ACADEMIC_YEAR_ID) {
        academicYearId = userData.USER_INFO[0].CURRENT_ACADEMIC_YEAR_ID;
      }
    }

    request.headers['academic-year-id'] = academicYearId || 0;

    request.headers['SOURCE'] = 'STAFF';
    request.headers['Ip-Address'] = (customProps?.ipAddress || getCachedItem('IP_ADDRESS') || '');

    return request;
  });

  const newgetNewRefreshToken = failedRequest => {
    const userData = getCachedUserData();
    if (!userData) {
      resetLocalStorage();
      window.location.replace('/login');
      return Promise.reject(new Error('No user data available'));
    }

    // Build headers matching the interceptor headers for normal requests
    const refreshHeaders: Record<string, any> = {
      Authorization: `Bearer ${userData.REFRESH_TOKEN}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'database': customProps?.dbName || getCachedItem('_DATABASE_') || '',
      'SOURCE': 'STAFF',
      'Ip-Address': customProps?.ipAddress || getCachedItem('IP_ADDRESS') || '',
    };

    if (userData.PAYMENT_SERVICE_NAME) {
      refreshHeaders['payment-service-name'] = userData.PAYMENT_SERVICE_NAME;
    }
    if (userData.INSTITUTE_ID) {
      refreshHeaders['institute-id'] = userData.INSTITUTE_ID;
    }
    if (userData.USER_NAME) {
      refreshHeaders['USER-NAME'] = userData.USER_NAME;
    }
    if (userData.USER_ROLE_NAME) {
      refreshHeaders['USER-ROLE'] = userData.USER_ROLE_NAME;
    }
    if (userData.STAFF_ROLE?.STAFF_ROLE_NAME) {
      refreshHeaders['STAFF-ROLE-NAME'] = userData.STAFF_ROLE.STAFF_ROLE_NAME;
    }
    if (userData.BRANCH_ID) {
      refreshHeaders['branch-id'] = userData.BRANCH_ID;
    }
    if (userData.USER_ID) {
      refreshHeaders['USER-ID'] = userData.USER_ID;
    }
    if (userData.BRANCH_DID_NUMBER) {
      refreshHeaders['branch-did-number'] = userData.BRANCH_DID_NUMBER;
    }

    // Handle academic year
    const selectedAcademicYear = getCachedItem('_SELECTED_ACADEMIC_YEAR_');
    let academicYearId = null;
    if (selectedAcademicYear) {
      try {
        academicYearId = JSON.parse(selectedAcademicYear as string)?.ACADEMIC_YEAR_ID;
      } catch (e) { }
    }
    if (!academicYearId && userData?.CURRENT_ACADEMIC_YEAR_ID) {
      academicYearId = userData.CURRENT_ACADEMIC_YEAR_ID;
    }
    if (academicYearId) {
      refreshHeaders['academic-year-id'] = academicYearId;
    }

    return axiosInstancenew
      .post(
        `/auth/refreshToken`,
        {
          USER_ID: userData.USER_ID,
          USER_LOGIN_ID: userData.USER_LOGIN_ID,
        },
        {
          headers: refreshHeaders,
        },
      )
      .then(response => {
        const newToken = response?.data?.data.new_token;
        setTokenToLocalStorage(newToken);

        // Update cache with new token
        headerCache.set('_UFT_', `Bearer ${newToken}`);

        failedRequest.response.config.headers['Authorization'] = 'Bearer ' + newToken;
        return refreshStoredGrantedRoleAccess(newToken)
          .catch(err => {
            console.error('Unable to refresh granted role access:', err);
          })
          .then(() => Promise.resolve());
      })
      .catch(err => {
        resetLocalStorage();
        headerCache.clear(); // Clear cache on logout
        window.location.replace('/login');
        return Promise.reject(err);
      });
  };

  createAuthRefreshInterceptor(axiosInstance, newgetNewRefreshToken, {
    retryInstance: axiosInstance,
  });

  axiosInstance.interceptors.response.use(
    function (response) {
      endRequest();

      const keywordsToCheck = [
        '/sync',
        '/save',
        '/upsert',
        '/TransportPickupPoint/mapPickupPointStudents',
        'changePassword',
        '/studentPromotion',
        'userDetail/update',
        'insert',
        'facebook/update_page'
      ];

      const containsKeywords = keywordsToCheck.some(keyword =>
        response?.request?.responseURL?.includes(keyword),
      );

      if (containsKeywords &&
        !response?.config?.suppressSuccessNotification &&
        [200, 201, 202]?.includes(response?.data?.code)) {
        if (!response?.request?.responseURL?.includes('/ChatConversation') &&
          !response?.request?.responseURL?.includes('/event/upsert') &&
          !response?.request?.responseURL?.includes('/branchNotification')) {
          success(response?.data?.message, true);
        }
      }

      if (response?.data?.code && ![200, 201, 202]?.includes(response?.data?.code)) {
        if (Array.isArray(response?.data?.error) && !!response?.data?.error?.length) {
          let messages: any = [];
          for (let i = 0; i < response?.data?.error?.length; i++) {
            if (response?.data?.message === 'FeeSchedule Response') {
              messages.push(
                (response?.data?.error[i]?.cause ||
                  response?.data?.error[i]?.message) + (i > 0 ? ' , ' : ''),
              );
            } else {
              messages.push(
                (response?.data?.error[i]?.message ||
                  response?.data?.error[i]?.cause) + (i > 0 ? ' , ' : ''),
              );
            }
          }

          if (response?.request?.responseURL?.includes("data/hallTicket/generate") &&
            response?.data?.message === "HallTicket Settings not found") {
            error(ErrorMessageFunction({
              basicPath: "ExamHallTicketSetting",
              customHandler: "/exam-setup/ExamHallTicketSetting/ExamHallTicketSetting/list"
            }), true);
          } else if (response?.request?.responseURL?.includes("data/reportCard/generate") &&
            response?.data?.message === "ReportCardSettings not found") {
            error(ErrorMessageFunction({
              basicPath: "ExamReportCardSetting",
              customHandler: "/exam-setup/ExamReportCardSetting/ExamReportCardSetting/list"
            }), true);
          } else {
            if (response?.data?.code !== 409) {
              error(messages, true);
            }
          }
          const err: any = new Error(messages || '');
          err.response = response;
          throw err;
        }
        const err: any = new Error(response?.data?.message || '');
        err.response = response;
        throw err;
      }

      return response;
    },
    err => {
      endRequest();

      // Answered specifically; the generic path reads as "the app is broken".
      if (isSubscriptionRestricted(err)) {
        handleSubscriptionRestricted(err);
        return Promise.reject(err);
      }

      if (err?.code !== 'ERR_CANCELED') {
        const errorMessage = err?.response?.data?.detail?.message || err?.response?.data?.message;
        const finalMessage = String(errorMessage || err?.message || err);
        const onDigitalMarketingDashboard = window.location.pathname.includes('/digital-marketing/dashboard');
        const isSocialHubError = finalMessage.toLowerCase().includes('social hub not connected');

        if (currentPath !== '/' && !(onDigitalMarketingDashboard && isSocialHubError)) {
          if (isSocialHubError) {
            warning(finalMessage, true);
          } else {
            error(finalMessage, true);
          }
        }
        return Promise.reject(err);
      }
    },
  );

  // Function to cancel all pending requests
  axiosInstance.cancelAllRequests = () => {
    cancelTokens.forEach(source => {
      source.cancel('');
    });
    cancelTokens.length = 0;
  };

  // Function to clear header cache (useful when user data changes)
  axiosInstance.clearHeaderCache = () => {
    headerCache.clear();
  };

  // Function to refresh cache
  axiosInstance.refreshCache = () => {
    preloadCache();
    getCachedUserData();
  };

  return axiosInstance;
};

export default httpclient;
